import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { CSSProperties, Children, ReactElement, ReactNode } from 'react';

import getComponentType from '../../helpers/react/getComponentType';
import wrapChildren from '../../helpers/react/wrapChildren';
import HiddenField from '../HiddenField/HiddenField';

import styles from './Stack.module.css';

type StackIgnoreProps = {
    children: ReactNode;
};

const StackIgnore = ({ children }: StackIgnoreProps) => children;

type StackProps = {
    space?:
        | 'none'
        | 'xxsmall'
        | 'xsmall'
        | 'small'
        | 'medium'
        | 'large'
        | 'large-plus'
        | 'xlarge'
        | 'xxlarge';
    exclude?: Array<ReactElement['type']>;
    excludeAboveMobile?: Array<ReactElement['type']>;
    flex?: boolean | number;
    animatePresence?: boolean;
    children?: ReactNode;
};

const Stack = ({
    space = 'medium',
    exclude = [],
    excludeAboveMobile = [],
    animatePresence = false,
    flex,
    children,
}: StackProps) => {
    const classes = classNames({
        [styles.Root]: true,
        [styles.spaceXXsmall]: space === 'xxsmall',
        [styles.spaceXsmall]: space === 'xsmall',
        [styles.spaceSmall]: space === 'small',
        [styles.spaceMedium]: space === 'medium',
        [styles.spaceLarge]: space === 'large',
        [styles.spaceLargePlus]: space === 'large-plus',
        [styles.spaceXlarge]: space === 'xlarge',
        [styles.spaceXxlarge]: space === 'xxlarge',
    });

    exclude.push(StackIgnore);
    exclude.push(HiddenField);

    const style: CSSProperties = {};
    if (flex) {
        style.flex = flex === true ? 1 : flex;
    }

    const childrenArray = Children.toArray(children);
    if (childrenArray.length === 0) {
        return null;
    }

    const childrenInLayers = wrapChildren(childrenArray, (child, key) => {
        const type = getComponentType(child);
        if (type && exclude?.indexOf(type) >= 0) {
            return <div key={key}>{child}</div>;
        }

        const classes = classNames(
            styles.Layer,
            type && excludeAboveMobile?.indexOf(type) >= 0 && styles.mobileOnly
        );

        return (
            <div className={classes} key={key}>
                {child}
            </div>
        );
    });

    return (
        <div className={classes} style={style}>
            {animatePresence ? (
                <AnimatePresence>{childrenInLayers}</AnimatePresence>
            ) : (
                childrenInLayers
            )}
        </div>
    );
};

Stack.Ignore = StackIgnore;

export default Stack;
